import { CheckIcon, ChevronsUpDown } from 'lucide-react'
import type * as React from 'react'
import { useState } from 'react'
import * as RPNInput from 'react-phone-number-input'
import flags from 'react-phone-number-input/flags'

import { Button } from '#components/ui/button'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '#components/ui/command'
import { Input } from '#components/ui/input'
import { Popover, PopoverContent, PopoverTrigger } from '#components/ui/popover'
import { ScrollArea } from '#components/ui/scroll-area'
import { cn } from '#lib/utils'

type PhoneInputProps = Omit<React.ComponentProps<typeof RPNInput.default>, 'onChange'> & {
  className?: string
  onChange?: (value: RPNInput.Value) => void
}

const PhoneInput = ({ className, onChange, ref, ...props }: PhoneInputProps) => {
  return (
    <RPNInput.default
      ref={ref}
      className={cn('shadow-custom flex rounded-3xl', className)}
      flagComponent={FlagComponent}
      countrySelectComponent={CountrySelect}
      inputComponent={InputComponent}
      smartCaret={false}
      defaultCountry="FR"
      /**
       * Handles the onChange event.
       *
       * react-phone-number-input might trigger the onChange event as undefined
       * when a valid phone number is not entered. To prevent this,
       * the value is coerced to an empty string.
       *
       * @param {E164Number | undefined} value - The entered value
       */
      onChange={(value) => {
        onChange?.(value ?? ('' as RPNInput.Value))
      }}
      {...props}
    />
  )
}

PhoneInput.displayName = 'PhoneInput'

const InputComponent = ({ className, ref, ...props }: React.ComponentProps<'input'>) => (
  <Input className={cn('rounded-s-none shadow-none', className)} {...props} ref={ref} />
)
InputComponent.displayName = 'InputComponent'

interface CountryEntry {
  label: string
  value: RPNInput.Country | undefined
}

interface CountrySelectProps {
  disabled?: boolean
  value: RPNInput.Country
  options: CountryEntry[]
  onChange: (country: RPNInput.Country) => void
}

const CountrySelect = ({
  disabled,
  value: selectedCountry,
  options: countryList,
  onChange,
}: CountrySelectProps) => {
  const [open, setOpen] = useState(false)
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="none"
          className="flex gap-1 rounded-e-none rounded-s-3xl border border-r-0 border-neutral-200 px-3 focus:z-10"
          disabled={disabled}
        >
          <FlagComponent country={selectedCountry} countryName={selectedCountry} />
          <ChevronsUpDown
            className={cn('-mr-2 size-4 opacity-50', disabled ? 'hidden' : 'opacity-100')}
          />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        data-phone-country-picker=""
        align="start"
        collisionPadding={16}
        className="w-[300px] max-w-[calc(100vw-32px)] p-0"
      >
        <Command>
          <CommandInput placeholder="Search country..." />
          <CommandList className="max-h-none">
            <ScrollArea className="h-[min(18rem,calc(var(--radix-popover-content-available-height)-3rem))]">
              <CommandEmpty>No country found.</CommandEmpty>
              <CommandGroup>
                {countryList.map(({ value, label }) =>
                  value ? (
                    <CountrySelectOption
                      key={value}
                      country={value}
                      countryName={label}
                      selectedCountry={selectedCountry}
                      onChange={(country) => {
                        onChange(country)
                        setOpen(false)
                      }}
                    />
                  ) : null
                )}
              </CommandGroup>
            </ScrollArea>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}

interface CountrySelectOptionProps extends RPNInput.FlagProps {
  selectedCountry: RPNInput.Country
  onChange: (country: RPNInput.Country) => void
}

const CountrySelectOption = ({
  country,
  countryName,
  selectedCountry,
  onChange,
}: CountrySelectOptionProps) => {
  return (
    <CommandItem
      className="gap-2"
      onSelect={() => {
        onChange(country)
      }}
    >
      <FlagComponent country={country} countryName={countryName} />
      <span className="flex-1 text-sm">{countryName}</span>
      <span className="text-foreground/50 text-sm">{`+${RPNInput.getCountryCallingCode(country)}`}</span>
      <CheckIcon
        className={`ml-auto size-4 ${country === selectedCountry ? 'opacity-100' : 'opacity-0'}`}
      />
    </CommandItem>
  )
}

const FlagComponent = ({ country, countryName }: RPNInput.FlagProps) => {
  const Flag = flags[country]

  return (
    <span className="bg-foreground/20 flex h-4 w-6 overflow-hidden rounded-sm [&_svg]:size-full">
      {Flag && <Flag title={countryName} />}
    </span>
  )
}

export { PhoneInput }
